/* eslint-disable formatjs/enforce-id */
import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  Toggle,
} from 'carbon-components-react';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './WatsonxCodeAssistantPE.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import { FormModal } from 'src/common/utils/FormModal';
import Footnote from './Footnote';
import Thanks from './Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import { LangDefaults, useLang } from 'src/common/hooks/widgets/useLang';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from '../../../common/utils/swapCountryAndLanguage';
import messages from '../locales/messages';
import { usePrice } from '../hooks/usePrice';

import {
  UT30PA,
  maxAnsibleDeveloper,
  minAnsibleDeveloper,
  defaultAnsibleDeveloper,
  minAnsibleTask,
  maxAnsibleTask,
  defaultAnsibleTask,
  defaultNumberPerYear,
  minNumberPerYear,
  maxNumberPerYear,
  tokenPerTask,
} from '../constants/defaults';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import countryList from '../constants/currencies.json';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';

export interface watsonxCodeAssistantPEProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const WatsonxCodeAssistantPE = ({
  langCode,
  test,
  countryToTest,
}: watsonxCodeAssistantPEProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [toggleActivated, setToggleActivated] = useState(true);
  const [currency, setCurrency] = useState('');
  const [ansibleDevelopers, setAnsibleDevelopers] = useState(
    defaultAnsibleDeveloper,
  );
  const [ansibleTask, setAnsibleTask] = useState(defaultAnsibleTask);
  const [timePerYear, setTimePerYear] = useState(defaultNumberPerYear);
  const [tierSuggested, setTierSuggested] = useState(
    messages.tier_essential.defaultMessage,
  );
  const [instanceFee, setInstanceFee] = useState(0);
  const [tunningHours, setTunningHours] = useState(0);
  const [excessRU, setExcessRU] = useState(0);
  const [countryCode, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [ruPriceEssential, setRUPriceEssential] = useState(0);
  const [ruPriceStandard, setRUPriceStandard] = useState(0);
  const [tuPrice, setTUPrice] = useState(0);
  const [feePrice, setFeePrice] = useState(0);

  const [totalMonthly, setTotalMonthly] = useCurrency(currency, langCode, 0);
  const [urxlang] = useState(localeCode.toString());

  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bpConsentMail] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const intl = useIntl();

  const { bpName, companyId, logo, desc } = useBPName(UT30PA, true);
  const cookieURL = useCookiesURL();

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;

  usePrice({
    currency,
    localeCode,
    countryCode,
    setRUPriceEssential,
    setRUPriceStandard,
    setTUPrice,
    setFeePrice,
  });

  useEffect(() => {
    setCookie();
  }, [
    ansibleDevelopers,
    ansibleTask,
    timePerYear,
    totalMonthly,
    bpConsentMail,
    bpName,
  ]);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' | Ansible Automation Platform: ' +
            toggleActivated +
            'Number of ansible developers:' +
            ansibleDevelopers +
            ' | Number of ansible task: ' +
            ansibleTask +
            ' | Tunning hours per Year: ' +
            timePerYear +
            ' | Estimated monthly cost: ' +
            totalMonthly +
            (getBp() ? ` | Preferred BP: ${getBp()}` : ''), // BP Name
        },
      }),
    );
  };
  console.log(Cookies.get('urxdcq'));

  const getBp = () => {
    if (bpName) return bpName;
    const bpField = document.querySelector<HTMLInputElement>('#Q_BPNAME');
    return bpField ? bpField.value : '';
  };

  const hideBox = () => {
    if (!bpName) {
      const bp_checkbox = Array.from(
        document.getElementsByClassName(
          'field-container Q_MAIL_CONSENT-container input-type-checkbox',
        ) as HTMLCollectionOf<HTMLElement>,
      )[0];
      if (bp_checkbox) bp_checkbox.style.display = 'none';
    }
  };

  const showBox = () => {
    const bpField = document.getElementById('Q_BPNAME');
    bpField?.addEventListener('keyup', (e) => {
      const bp_checkbox = Array.from(
        document.getElementsByClassName(
          'field-container Q_BPCONSENT-container input-type-checkbox',
        ) as HTMLCollectionOf<HTMLElement>,
      )[0];
      if ((e.target as HTMLInputElement).value) {
        bp_checkbox.style.display = 'unset';
      } else {
        bp_checkbox.style.display = 'none';
      }
    });
  };

  const replaceBpField = () => {
    const bpField = document.getElementById('Q_BPNAME') as HTMLInputElement;

    if (bpName && bpField) {
      //update label text
      const bpLabel = bpField.labels?.length
        ? bpField.labels[0]
        : new HTMLInputElement();
      bpLabel.innerHTML = intl.formatMessage(messages.preferred_bp);

      //replace the bp field by the bp name
      const bpText = document.createElement('p') as HTMLInputElement;
      bpText.innerText = bpName;
      bpText.style.fontSize = '1rem';
      bpText.style.fontWeight = 'bold';
      bpText.style.lineHeight = '2.5';
      bpField.parentNode?.replaceChild(bpText, bpField);
    }
  };

  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    if (value >= 1 && value <= max) {
      setValue(value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  const setCTA = () => {
    if (ansibleDevelopers < 61 && toggleActivated) {
      return (
        <>
          <ButtonSet className="btn-set mar4" stacked>
            <Button
              onClick={() => {
                segmentTracking('CTA Clicked', {
                  pageTitle: 'watsonx Code Assistant Page',
                  object: 'Pricing page',
                  resultValue: 'CTA clicked',
                  CTA: 'Sign up for WCA Trial',
                  location: 'Estimator',
                  action: '',
                  field: '',
                });
              }}
              target="_blank"
              href="https://dataplatform.cloud.ibm.com/registration/stepone?context=wca&apps=wca,cos&regions=us-south&uucid=0e2742f903313c90&utm_content=WACWW&content_campaign_code=WACWW"
            >
              <FormattedMessage {...messages.CTA_02} />
            </Button>
          </ButtonSet>
        </>
      );
    } else {
      return (
        <>
          <ButtonSet className="watsonx_code_assistants__stack-button" stacked>
            <Button
              id="request_btn"
              onClick={() => {
                segmentTracking('CTA Clicked', {
                  pageTitle: 'watsonx Code Assistant Page',
                  object: 'Pricing page',
                  resultValue: 'CTA clicked',
                  CTA: 'Book a meeting',
                  location: 'Estimator',
                  action: '',
                  field: '',
                });
                initUrxForm(
                  environment,
                  URX_FORM.INSTANCE_ID,
                  URX_FORM.FORM_ID,
                  urxlang as IBMLocale,
                  companyId,
                );
                setDrawerOpen(true);
                replaceBpField();
                hideBox();
                showBox();

                setCookie();
              }}
            >
              <FormattedMessage {...messages.CTA_01} />
            </Button>
          </ButtonSet>
        </>
      );
    }
  };

  useEffect(() => {
    const RU = Math.ceil(
      (ansibleDevelopers * ansibleTask * tokenPerTask) / 10000,
    );
    const RUExces = RU - 660 < 0 ? 0 : RU - 660;
    const Fee = Math.ceil(1 * feePrice);

    const EssentialPrice = Math.ceil(RU * ruPriceEssential);
    const StandardPrice = Math.ceil(
      Fee + RUExces * ruPriceStandard + timePerYear * tuPrice,
    );

    if (ansibleDevelopers > 60 || ansibleTask >= 12000) {
      setTierSuggested(messages.tier_standard.defaultMessage);
      setTunningHours(Math.ceil(timePerYear * tuPrice));
      setTotalMonthly(StandardPrice);
      setInstanceFee(Fee);
      setExcessRU(Math.ceil(RUExces * ruPriceStandard));
    } else {
      setTierSuggested(messages.tier_essential.defaultMessage);
      setTunningHours(0);
      setExcessRU(0);
      setInstanceFee(0);
      setTotalMonthly(EssentialPrice);
    }

    console.log('RU calculated: ', RU);
    console.log('RU Exceded: ', excessRU);
    console.log('Essential Price:', ruPriceEssential);
    console.log('Standard Price:', ruPriceStandard);
    console.log('Tuning Hours:', tuPrice);
    console.log('Fee Price:', feePrice);
    console.log('Essential Price: ', EssentialPrice);
    console.log('Standard Price: ', StandardPrice);
  }, [
    ansibleTask,
    ansibleDevelopers,
    timePerYear,
    ruPriceEssential,
    ruPriceStandard,
    tuPrice,
    feePrice,
  ]);

  return (
    <div className="watsonx_code_assistant widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          timePerYear={timePerYear}
          ansibleTask={ansibleTask}
          ansibleDeveloper={ansibleDevelopers}
          licensePrice={totalMonthly.toString()}
          bpName={bpName}
          bpConsentMail={bpConsentMail}
        />
      ) : (
        <div id="WXOCPEGrid" className="bx--grid widget-reset">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <p className="page_sub_heading">
                    {' '}
                    <FormattedMessage {...messages.pa_subheadline} />
                  </p>
                  {/*  Toggle section  */}
                  <div className="bx--row padding_8">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Toggle tooltip',
                            location: 'Estimator',
                            action: '0',
                            field: '0',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage {...messages.toggle_tittle} />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages.toggle__tooltip} />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row padding_8">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 toggle-row">
                      <Toggle
                        size="md"
                        labelA={messages.toggle_no.defaultMessage}
                        labelB={messages.toggle_yes.defaultMessage}
                        id="APP"
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Ansible automation platform toggle',
                            location: 'Estimator',
                            action: '0',
                            field: '0',
                          });
                          setToggleActivated(!toggleActivated);
                        }}
                      />
                    </div>
                  </div>
                  {/* Slider sections */}
                  {/* Number-Ansible-Developers */}
                  <div className="bx--row padding_8">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Ansible developer tooltip',
                            location: 'Estimator',
                            action: '0',
                            field: '0',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages.labeltext_ansibleDeveloper}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage
                            {...messages.tooltip_ansibleDeveloper}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="number-ansible-developers"
                        min={minAnsibleDeveloper}
                        hideTextInput={false}
                        inputType="number"
                        max={maxAnsibleDeveloper}
                        value={ansibleDevelopers}
                        onBlur={() => {
                          null;
                        }}
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total number of ansible developer - Slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(
                            value,
                            setAnsibleDevelopers,
                            maxAnsibleDeveloper,
                            minAnsibleDeveloper,
                          );
                          if (value !== 0) {
                            setAnsibleDevelopers(value);
                          } else {
                            setAnsibleDevelopers(minAnsibleDeveloper);
                          }
                        }}
                      />
                    </div>
                    <div></div>
                  </div>

                  {/*  Number-Ansible-task  */}
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Number of ansible task tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages.labeltext_ansibleTask}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages.tooltip_ansibleTask} />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        hideTextInput={false}
                        id="number-ansible-task"
                        min={minAnsibleTask}
                        max={maxAnsibleTask}
                        onBlur={() => {
                          null;
                        }}
                        inputType="number"
                        value={ansibleTask}
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx Code Assistant Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'number of ansible task - Slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(
                            value,
                            setAnsibleTask,
                            maxAnsibleTask,
                            minAnsibleTask,
                          );
                          if (value !== 0) {
                            setAnsibleTask(value);
                          } else {
                            setAnsibleTask(minAnsibleTask);
                          }
                        }}
                      />
                    </div>
                    <div></div>
                  </div>
                  {/*  Number-tuning-per-year  */}
                  {ansibleDevelopers > 60 ? (
                    <>
                      <div className="bx--row mar3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'watsonx Code Assistant Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Number of tuning hours tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            className=""
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <div className="page_body">
                                <FormattedMessage
                                  {...messages.labeltext_numberPerYear}
                                />
                              </div>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.tooltip_numberPerYear}
                              />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="bx--row mar1">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <Slider
                            hideTextInput={false}
                            id="number-timer-per-year"
                            min={minNumberPerYear}
                            max={maxNumberPerYear}
                            onBlur={() => {
                              null;
                            }}
                            value={timePerYear}
                            inputType="number"
                            onChange={({ value }) => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'watsonx Code Assistant Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Number of tuning hours - Slider',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              handleSliders(
                                value,
                                setTimePerYear,
                                maxNumberPerYear,
                                minNumberPerYear,
                              );
                              if (value !== 0) {
                                setTimePerYear(value);
                              } else {
                                setTimePerYear(minNumberPerYear);
                              }
                            }}
                          />
                        </div>
                        <div></div>
                      </div>
                    </>
                  ) : (
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16"></div>
                    </div>
                  )}
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          <FormattedMessage {...messages.footnote_one} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* ======================================================== */}
                {/** Table section **/}
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                  <div id="stickyNav" className="bx--row">
                    <div className="sticky-nav price_table_comp">
                      <table className="cds--data-table cds--data-table--sm">
                        <thead>
                          <tr>
                            <th scope="col">
                              <div className="price_table_comp__heading">
                                <FormattedMessage
                                  {...messages.suggested_title}
                                />
                              </div>
                            </th>
                            <th scope="col">
                              <div className="cds--table-header-label">
                                <div className=" price_table_comp__heading">
                                  {tierSuggested}
                                </div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody aria-live="polite">
                          <tr>
                            <td>
                              {' '}
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage
                                  {...messages.instance_title}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">{`${instanceFee}`}</div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage {...messages.excess_title} />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">{`${excessRU}`}</div>
                            </td>
                          </tr>

                          <tr>
                            <td>
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage {...messages.tunning_title} />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">{`${tunningHours}`}</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      {/* <div className="empty"></div> */}
                      {timePerYear > 0 &&
                      tierSuggested ===
                        messages.tier_essential.defaultMessage ? (
                        <div id="contact_toggle">
                          <p
                            id="widget.contact_info"
                            className="price_table_comp__disclaimer mar4"
                          >
                            <FormattedMessage {...messages.error_price} />
                          </p>
                          {setCTA()}
                        </div>
                      ) : (
                        <div id="price_toggle_one">
                          <p
                            id="widget.estimated-cost"
                            className="estmt-cost price_table_comp__bodycopy padding_24_2"
                          >
                            <FormattedMessage {...messages.estimated_price} />
                            <sup className="watsonx_code_assistant__sup">1</sup>
                          </p>
                          <p className="padding_8 price_table_comp__price">
                            {totalMonthly}*
                          </p>
                          <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                            * <FormattedMessage {...messages.asterisk_price} />
                          </p>
                          {setCTA()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div id="footnote" className="div-only-media">
                    <div className="bx--row mar6">
                      <div className="bx--col-lg-9">
                        <div>
                          <Footnote numNote={1} />
                        </div>
                        <div className="footnote_text">
                          <FormattedMessage {...messages.footnote_one} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr
                className="watsonx_code_assistant__custom-hr"
                id="hr_row_two"
              />
            </div>
          </div>
        </div>
      )}
      <FormModal
        pageTitle="watsonx Code Assistant PE Page"
        object="watsonx Code Assistant PE Page"
        productTitle={'watsonx Code Assistant price estimator'}
        pageUrl={''}
        location={'Estimator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formId={URX_FORM.FORM_ID}
        lang={lang}
      ></FormModal>
    </div>
  );
};

export default WatsonxCodeAssistantPE;
